// Customer portal — chrome (host shell).
//
// `mount(rootEl)` is the entire entry point. It:
//   1. Reads a magic-link token from the URL (?token= or #token=).
//   2. With no token, renders the welcome / "use your retailer's link" screen.
//   3. With a token, fetches the customer project from the backend.
//        • on 401/404 → renders an "expired link" error screen.
//        • on network/transport error in dev → falls back to a demo project
//          so sibling section modules can be developed offline.
//   4. Applies retailer branding (CSS vars, page title, favicon).
//   5. Builds the header + section stack + toast region.
//   6. Mounts each registered section that wants to render for this project.
//
// Customer portal is a single-page experience: there is no navigation
// teardown to perform. Page hide / reload disposes of everything.

import { api, ApiError } from "@tkos/api-client";
import type {
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";
import { buildHeader } from "./header";
import { buildFeedbackFooter } from "./feedback-link";
import { mountAiWidget } from "./ai-widget";
import { maybeStartCustomerDemo } from "./demo";
import {
  applyBranding,
  loadCustomerProject,
  loadCustomerProjectFromSession,
} from "./project-loader";
import { renderLogin } from "./login";
import { showSetPasswordPrompt } from "./set-password";
import { showTermsGate } from "./terms-gate";
import {
  buildDrawerBackdrop,
  buildHamburger,
  buildMobileDrawer,
  buildTabbar,
  computeBadges,
  NAV_EVENT_NAME,
  NAV_TABS,
  readPageFromHash,
  setActiveDrawerItem,
  setActiveTab,
  writePageToHash,
  type NavEventDetail,
} from "./nav";
import { SECTIONS } from "./sections-registry";
import { buildToastRegion, createToastFn } from "./toast";

// Note: `./styles.css` is imported from `../main.ts` so the chrome's
// stylesheet ships even if `mount()` short-circuits to the welcome screen.

// ── Token parsing ────────────────────────────────────────────────────────────

/** Read the magic-link token from the URL. Prefers `?token=`, falls back to `#token=`. */
function readToken(): string | null {
  // 1. Query string.
  try {
    const qs = new URLSearchParams(window.location.search);
    const t = qs.get("token");
    if (t && t.trim() !== "") return t.trim();
  } catch {
    /* ignore */
  }

  // 2. Hash fragment ("#token=..." or "#/foo?token=...").
  const hash = window.location.hash.replace(/^#/, "");
  if (hash) {
    const idx = hash.indexOf("?");
    const hashQs = idx >= 0 ? hash.slice(idx + 1) : hash;
    try {
      const params = new URLSearchParams(hashQs);
      const t = params.get("token");
      if (t && t.trim() !== "") return t.trim();
    } catch {
      /* ignore */
    }
  }

  return null;
}

// ── Static screens (welcome / error / loading) ──────────────────────────────

function renderBootstrap(rootEl: HTMLElement, label: string): void {
  rootEl.replaceChildren();
  const node = document.createElement("div");
  node.className = "cust-bootstrap";
  node.textContent = label;
  rootEl.appendChild(node);
}

function renderFullscreen(
  rootEl: HTMLElement,
  opts: { title: string; body: string; cta?: { label: string; href: string } | null },
): void {
  rootEl.replaceChildren();

  const wrap = document.createElement("div");
  wrap.className = "cust-fullscreen";

  const card = document.createElement("div");
  card.className = "cust-fullscreen-card";

  const h = document.createElement("h1");
  h.className = "cust-fullscreen-title";
  h.textContent = opts.title;

  const p = document.createElement("p");
  p.className = "cust-fullscreen-body";
  p.textContent = opts.body;

  card.append(h, p);

  if (opts.cta) {
    const a = document.createElement("a");
    a.className = "cust-fullscreen-cta";
    a.href = opts.cta.href;
    a.textContent = opts.cta.label;
    card.appendChild(a);
  }

  wrap.appendChild(card);
  rootEl.appendChild(wrap);
}

function renderInvalidLink(rootEl: HTMLElement, retailerEmail: string | null): void {
  renderFullscreen(rootEl, {
    title: "This link has expired or is invalid",
    body:
      "Your access link is no longer valid. Please ask your retailer to send you a fresh " +
      "link — it usually arrives by email or text within a few minutes.",
    cta: retailerEmail
      ? { label: "Contact your retailer", href: `mailto:${retailerEmail}` }
      : { label: "Reload this page", href: window.location.pathname },
  });
}

// ── Project rendering ────────────────────────────────────────────────────────

interface MountedSection {
  section: CustomerSection;
  cleanup: SectionCleanup;
  container: HTMLElement;
}

function renderProject(
  rootEl: HTMLElement,
  project: CustomerProject,
  refresh: () => Promise<void>,
): MountedSection[] {
  rootEl.replaceChildren();
  applyBranding(project);

  // Outer flex column (header on top, scroll below, toasts overlaid).
  const app = document.createElement("div");
  app.className = "cust-app";

  const toastRegion = buildToastRegion();
  const showToast = createToastFn(toastRegion);

  const header = buildHeader(project.retailer, project.customer, showToast);

  const main = document.createElement("main");
  main.className = "cust-shell-main";
  main.setAttribute("role", "main");

  // Build context first — sections receive it on mount.
  const ctx: SectionContext = {
    api,
    project,
    showToast,
    refresh,
  };

  // ── Mount sections into hidden containers ────────────────────────────
  //
  // 2026-05-19: customer portal switched from a single vertical scroll
  // to a tabbar-based router (parity with the source-of-truth at
  // customer-standalone.html:569-577). Every registered section mounts
  // once; only the section matching `currentPage` carries the `--active`
  // class. Switching is presentational — sections keep their state
  // (polls, scroll positions, drafts) across tab changes.
  const mounted: MountedSection[] = [];
  const availableIds = new Set<string>();

  for (const section of SECTIONS) {
    let willRender = true;
    try {
      willRender = section.shouldRender(project);
    } catch (err) {
      console.warn(`[customer/shell] section "${section.id}" shouldRender threw`, err);
      willRender = false;
    }
    if (!willRender) continue;

    const wrapper = document.createElement("section");
    wrapper.className = "cust-section";
    wrapper.dataset["sectionId"] = section.id;
    wrapper.setAttribute("role", "tabpanel");
    wrapper.setAttribute("aria-labelledby", `cust-tab-${section.id}`);

    if (section.label) {
      const lbl = document.createElement("div");
      lbl.className = "cust-section-label";
      lbl.textContent = section.label;
      wrapper.appendChild(lbl);
    }

    const body = document.createElement("div");
    wrapper.appendChild(body);
    main.appendChild(wrapper);

    try {
      const cleanup = section.mount(body, ctx);
      mounted.push({ section, cleanup, container: wrapper });
      availableIds.add(section.id);
    } catch (err) {
      console.error(`[customer/shell] section "${section.id}" mount failed`, err);
      body.innerHTML = "";
      const errEl = document.createElement("div");
      errEl.className = "cust-error";
      errEl.textContent = `Couldn't load ${section.label || section.id}.`;
      body.appendChild(errEl);
    }
  }

  // ── Build + wire the tabbar (desktop) and mobile nav drawer ──────────
  const badges = computeBadges(project);
  const tabbar = buildTabbar(availableIds, badges);
  const drawer = buildMobileDrawer(availableIds, badges);
  const backdrop = buildDrawerBackdrop();
  const hamburger = buildHamburger();
  // Drop the hamburger at the start of the header's right cluster so on
  // mobile (where helpline/bell/help may be hidden) it lands next to the
  // user chip. Order is presentational; click handlers are wired below.
  const headerRight = header.querySelector<HTMLElement>(".cust-header-right");
  if (headerRight) headerRight.prepend(hamburger);

  const feedbackFooter = buildFeedbackFooter(project);
  app.append(header, tabbar, drawer, backdrop, main, feedbackFooter, toastRegion);
  rootEl.appendChild(app);

  // Retailer-branded AI assistant (bottom-right). Self-fetches its config and
  // hides itself if the retailer has disabled it. Appended to the app root.
  mountAiWidget(app, project.retailer?.businessName || "");

  // ── Mobile drawer open/close ────────────────────────────────────────
  function setDrawerOpen(open: boolean): void {
    drawer.classList.toggle("cust-nav-drawer--open", open);
    backdrop.classList.toggle("cust-nav-backdrop--open", open);
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    hamburger.setAttribute("aria-expanded", open ? "true" : "false");
    document.body.classList.toggle("cust-nav-locked", open);
  }
  const closeDrawer = (): void => setDrawerOpen(false);

  hamburger.addEventListener("click", () => {
    const isOpen = drawer.classList.contains("cust-nav-drawer--open");
    setDrawerOpen(!isOpen);
  });
  backdrop.addEventListener("click", closeDrawer);
  const onDrawerKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && drawer.classList.contains("cust-nav-drawer--open")) {
      closeDrawer();
      hamburger.focus();
    }
  };
  document.addEventListener("keydown", onDrawerKey);
  // Close drawer if the viewport grows past the mobile breakpoint while
  // the drawer is open (e.g. tablet rotation, dev-tools resize).
  const desktopMq = window.matchMedia("(min-width: 1024px)");
  const onMqChange = (e: MediaQueryListEvent): void => {
    if (e.matches) closeDrawer();
  };
  desktopMq.addEventListener("change", onMqChange);

  // Pick the initial active page. Order of preference:
  //   1. URL hash (#home, #?page=home) — supports deep-linking + back/forward.
  //   2. First NAV tab whose section actually mounted (graceful when
  //      e.g. a section is hidden for a new-lead project).
  function firstAvailableNavId(): string {
    const t = NAV_TABS.find((nt) => availableIds.has(nt.id));
    return t?.id ?? mounted[0]?.section.id ?? "";
  }

  function applyActivePage(page: string): void {
    // If the requested page didn't mount (e.g. shouldRender returned
    // false), fall back to the first available tab so the user always
    // sees something.
    const target = availableIds.has(page) ? page : firstAvailableNavId();
    for (const m of mounted) {
      const isActive = m.section.id === target;
      m.container.classList.toggle("cust-section--active", isActive);
    }
    setActiveTab(tabbar, target);
    setActiveDrawerItem(drawer, target);
    writePageToHash(target);
    // Scroll to the top of the main column — mirrors legacy navTo
    // behaviour (customer-live.js:167).
    if (main && typeof main.scrollIntoView === "function") {
      main.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  // Initial page.
  const initial = readPageFromHash() ?? firstAvailableNavId();
  applyActivePage(initial);

  // Tab click → switch.
  tabbar.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-page]");
    if (!t) return;
    const page = t.dataset["page"];
    if (page) applyActivePage(page);
  });

  // Drawer item click → switch + close drawer.
  drawer.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-page]");
    if (!t) return;
    const page = t.dataset["page"];
    if (page) {
      applyActivePage(page);
      closeDrawer();
    }
  });

  // Cross-section navigation: any section can dispatch a
  // `cust-nav` CustomEvent with `detail.page` and the shell handles it.
  // E.g. "Message your designer" button in project-overview.
  const onNavEvent = (e: Event): void => {
    const detail = (e as CustomEvent<NavEventDetail>).detail;
    if (detail && typeof detail.page === "string") {
      applyActivePage(detail.page);
    }
  };
  window.addEventListener(NAV_EVENT_NAME, onNavEvent as EventListener);

  // Back/forward — hashchange-driven.
  const onHashChange = (): void => {
    const page = readPageFromHash();
    if (page) applyActivePage(page);
  };
  window.addEventListener("hashchange", onHashChange);

  // Stash teardown handles on the first mounted entry so disposeMounted
  // can also detach these listeners. (Keeps the public type stable.)
  mounted.unshift({
    section: { id: "__shell-nav__", shouldRender: () => false, mount: () => () => {} },
    cleanup: () => {
      window.removeEventListener(NAV_EVENT_NAME, onNavEvent as EventListener);
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("keydown", onDrawerKey);
      desktopMq.removeEventListener("change", onMqChange);
      document.body.classList.remove("cust-nav-locked");
    },
    container: tabbar,
  });

  return mounted;
}

function disposeMounted(mounted: MountedSection[]): void {
  for (const { section, cleanup } of mounted) {
    try {
      cleanup();
    } catch (err) {
      console.warn(`[customer/shell] cleanup for "${section.id}" threw`, err);
    }
  }
}

// ── Public entry ─────────────────────────────────────────────────────────────

/**
 * Mount the customer portal chrome. Called once from main.ts.
 *
 * Customer portal is single-page; we don't return a teardown. We DO
 * listen for `pagehide` so any mounted section's cleanup runs (timers,
 * pollers, etc.) before the browser unloads.
 */
export async function mount(rootEl: HTMLElement): Promise<void> {
  // Scripted autopilot demo overlay (?demo=customer) — pure simulation, runs
  // over whatever the portal renders. Self-gates on the URL param.
  maybeStartCustomerDemo();

  const token = readToken();

  // 1. Magic-link path (PRIMARY) — a `?token=` URL exchanges for a session
  //    and loads the project. Any failure shows an explicit error screen.
  if (token) {
    await bootProject(
      rootEl,
      (signal) => loadCustomerProject(token, signal),
      () => renderInvalidLink(rootEl, null),
    );
    // First-use: a magic-link customer with no password yet is invited
    // (skippably) to set one so they can return via email + password. Self-
    // guarding: if the link was invalid there's no session, so this no-ops.
    void maybePromptFirstUsePassword();
    return;
  }

  // 2. No token — returning-customer path. Probe for an existing session
  //    cookie first (a customer who logged in earlier and still has it),
  //    otherwise show the email + password login screen.
  let signedIn = false;
  try {
    const probe = await api<{ ok: boolean }>("/customer/check", { method: "GET" });
    signedIn = !!probe.ok;
  } catch {
    signedIn = false;
  }

  if (signedIn) {
    await bootSession(rootEl);
    return;
  }

  renderLogin(rootEl, { onAuthenticated: () => void bootSession(rootEl) });
}

/** After a magic-link boot, prompt the customer to set a password if they
 *  haven't got one yet. Probes /customer/check (which now reports
 *  `hasPassword`); shows the skippable overlay only when signed in AND
 *  password-less. Never throws into the boot path. */
async function maybePromptFirstUsePassword(): Promise<void> {
  // Show at most once per browser session — otherwise the skippable overlay
  // re-appears on every manual reload for a password-less (magic-link) lead,
  // which reads as nagging. It returns next session until they set a password.
  try {
    if (sessionStorage.getItem("tkos.cust.setpw.seen")) return;
  } catch { /* sessionStorage unavailable — fall through and show once */ }
  try {
    const r = await api<{ ok: boolean; customer?: { hasPassword?: boolean } }>(
      "/customer/check",
      { method: "GET" },
    );
    if (r.ok && r.customer && r.customer.hasPassword === false) {
      try { sessionStorage.setItem("tkos.cust.setpw.seen", "1"); } catch { /* ignore */ }
      showSetPasswordPrompt();
    }
  } catch {
    /* never block the portal on this */
  }
}

/** Boot the portal from an authenticated session cookie (post-login or
 *  restored). On an expired/missing session, fall back to the login screen. */
async function bootSession(rootEl: HTMLElement): Promise<void> {
  await bootProject(
    rootEl,
    (signal) => loadCustomerProjectFromSession(signal),
    () => renderLogin(rootEl, { onAuthenticated: () => void bootSession(rootEl) }),
  );
}

/**
 * Shared project boot: render a loading state, fetch the project via the
 * supplied loader, wire a refresh handler + tab-unload cleanup, then mount
 * the section stack. `onAuthError` runs on a 401/404 (expired link or
 * session); any other failure shows the generic load-error screen.
 *
 * Real data only — a token/session that fails surfaces an explicit screen
 * rather than silently masking the failure with demo data.
 */
async function bootProject(
  rootEl: HTMLElement,
  load: (signal: AbortSignal) => Promise<CustomerProject>,
  onAuthError: () => void,
): Promise<void> {
  renderBootstrap(rootEl, "Loading your project…");

  const ac = new AbortController();
  let mounted: MountedSection[] = [];
  let pollHandle: ReturnType<typeof setInterval> | null = null;
  let lastSig = ""; // signature of the last-rendered project (change detection)

  // Signature for change detection. Normalises ISO-datetime VALUES to a constant
  // token so per-request server timestamps (e.g. an appointment's `requestedAt`
  // or a delivery's `at` that default to `new Date()` when the record has none)
  // don't make every poll look like a change. Real changes still register — new
  // records (new ids), status transitions, and null→timestamp presence flips all
  // survive the normaliser, so an appointment moving pending→confirmed still
  // triggers a remount.
  const sigOf = (p: CustomerProject): string =>
    JSON.stringify(p, (_k, v) =>
      typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) ? "·T·" : v);

  // Refresh handler — re-fetches project and re-renders all sections.
  // (We tear down + remount; sections own their own state.)
  const refresh = async (): Promise<void> => {
    try {
      const fresh = await load(ac.signal);
      disposeMounted(mounted);
      mounted = renderProject(rootEl, fresh, refresh);
      lastSig = sigOf(fresh);
    } catch (err) {
      console.warn("[customer/shell] refresh failed:", err);
    }
  };

  // Best-effort cleanup on tab unload.
  window.addEventListener(
    "pagehide",
    () => {
      ac.abort();
      if (pollHandle != null) clearInterval(pollHandle);
      disposeMounted(mounted);
    },
    { once: true },
  );

  let project: CustomerProject;
  try {
    project = await load(ac.signal);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
      onAuthError();
      return;
    }
    console.error("[customer/shell] failed to load project", err);
    renderLoadError(rootEl);
    return;
  }

  // First-open Terms & Conditions gate — BLOCKING. On a customer's very first
  // visit the server reports `termsAcceptedAt == null`; we must not render the
  // portal until they read and accept. Returns once acceptance is recorded.
  if (project.customer && !project.customer.termsAcceptedAt) {
    await showTermsGate(project.retailer);
    project.customer.termsAcceptedAt = new Date().toISOString();
  }

  mounted = renderProject(rootEl, project, refresh);

  // ── Shared auto-refresh (15s, change-detected) ───────────────────────────
  // Re-fetch the project on an interval and remount ONLY when the data has
  // actually changed, so new quotes / appointments / documents / status appear
  // without a manual reload — while the view stays still (scroll + active tab,
  // which renderProject restores from the URL hash) whenever nothing changed.
  // The messages section keeps its own faster in-place chat poll. Paused while
  // the tab is hidden; the interval is cleared in the pagehide handler above.
  // 5s (was 15s) so updates land near-instantly without a manual reload. The
  // customer portal has no WebSocket (unlike the retailer portal), so frequent
  // change-detected polling is how we approximate real-time. The poll is cheap
  // (one GET, no remount unless the data actually changed) and pauses while the
  // tab is hidden.
  const AUTO_REFRESH_MS = 5_000;
  lastSig = sigOf(project);
  let prevProject: CustomerProject = project;
  const maybeRefresh = async (): Promise<void> => {
    if (document.visibilityState === "hidden") return;
    try {
      const fresh = await load(ac.signal);
      const sig = sigOf(fresh);
      if (sig === lastSig) return; // nothing new → no remount, no flicker
      // Work out what changed BEFORE we remount, so we can surface a slide-in
      // toast for new messages / documents / designs the moment they arrive.
      const notices = diffNotices(prevProject, fresh);
      lastSig = sig;
      prevProject = fresh;
      disposeMounted(mounted);
      mounted = renderProject(rootEl, fresh, refresh);
      if (notices.length > 0) {
        // renderProject rebuilt the toast region; grab the fresh one and fire.
        const region = rootEl.querySelector<HTMLElement>(".cust-toast-region");
        if (region) {
          const toast = createToastFn(region);
          for (const n of notices) toast(n.msg, n.kind);
        }
      }
    } catch (err) {
      console.warn("[customer/shell] auto-refresh failed:", err);
    }
  };
  const startPolling = (): void => {
    if (pollHandle == null) pollHandle = setInterval(() => void maybeRefresh(), AUTO_REFRESH_MS);
  };
  const stopPolling = (): void => {
    if (pollHandle != null) { clearInterval(pollHandle); pollHandle = null; }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") stopPolling();
    else { void maybeRefresh(); startPolling(); }
  });
  startPolling();
}

/** Compare two successive project snapshots and produce slide-in toast
 *  notices for the changes a customer cares about (new message from their
 *  retailer, a new document or design shared with them). Returns an empty
 *  array when nothing notice-worthy changed. Kept deliberately narrow so the
 *  toast never fires on noise (e.g. status timestamps the signature already
 *  filters). */
function diffNotices(
  prev: CustomerProject,
  next: CustomerProject,
): Array<{ msg: string; kind: "info" | "success" }> {
  const out: Array<{ msg: string; kind: "info" | "success" }> = [];

  const prevUnread = typeof prev.unreadMessages === "number" ? prev.unreadMessages : 0;
  const nextUnread = typeof next.unreadMessages === "number" ? next.unreadMessages : 0;
  if (nextUnread > prevUnread) {
    const who = next.retailer?.businessName?.trim() || "your retailer";
    const more = nextUnread - prevUnread;
    out.push({
      msg: more > 1 ? `${more} new messages from ${who}` : `New message from ${who}`,
      kind: "info",
    });
  }

  if (next.documents.length > prev.documents.length) {
    out.push({ msg: "A new document was added to your project", kind: "info" });
  }
  if (next.designs.length > prev.designs.length) {
    out.push({ msg: "A new design was shared with you", kind: "info" });
  }

  return out;
}

/** Render an explicit "couldn't load" screen for non-401/404 failures on
 *  a token-bearing URL. Replaces the old silent demo-fallback so the
 *  customer never sees seeded demo data when they expect real data. */
function renderLoadError(rootEl: HTMLElement): void {
  renderFullscreen(rootEl, {
    title: "We couldn't load your project",
    body:
      "Something went wrong loading your kitchen project. Please reload " +
      "the page — if it keeps happening, ask your retailer to send a fresh link.",
    cta: { label: "Reload", href: window.location.href },
  });
}
